<template>
  <div class="behavioral-test">
    <div class="test-header">
      <h2>{{ test.name }}</h2>
      <div class="instructions" v-html="formattedInstructions"></div>
      <div class="progress-info">
        <div class="question-progress">
          Groupe {{ currentQuestionIndex + 1 }} sur {{ test.questions.length }}
        </div>
        <div class="time-remaining" v-if="timeRemaining">
          Temps restant : {{ formatTime(timeRemaining) }}
        </div>
      </div>
    </div>

    <div class="question-container" v-if="currentQuestion">
      <h3 class="group-title">{{ currentQuestion.text }}</h3>
      
      <div class="rating-grid">
        <div class="rating-header">
          <div class="trait-label">Caractéristiques</div>
          <div class="rating-numbers">
            <div class="rating-number">1</div>
            <div class="rating-number">2</div>
            <div class="rating-number">3</div>
            <div class="rating-number">4</div>
          </div>
        </div>

        <div class="rating-rows">
          <div v-for="answer in currentQuestion.answers" 
               :key="answer.id"
               class="rating-row">
            <div class="trait-text">{{ answer.text }}</div>
            <div class="rating-options">
              <div v-for="n in 4"
                   :key="n"
                   class="rating-cell"
                   :class="{
                     'selected': getRatingForAnswer(answer.id) === n
                   }"
                   @click="setRating(answer.id, n)">
                <div class="radio-button">
                  <div class="inner-circle" v-if="getRatingForAnswer(answer.id) === n"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="navigation-buttons">
      <button 
        class="btn btn-secondary" 
        @click="previousQuestion"
        :disabled="currentQuestionIndex === 0">
        <i class="fas fa-arrow-left"></i> Précédent
      </button>
      <button 
        class="btn btn-primary" 
        @click="nextQuestion"
        :disabled="!isCurrentGroupComplete">
        {{ isLastQuestion ? 'Terminer' : 'Suivant' }}
        <i :class="isLastQuestion ? 'fas fa-check' : 'fas fa-arrow-right'"></i>
      </button>
    </div>
  </div>
</template>

<script>


export default {
  name: 'TestBehavioralStyle',
  
  props: {
    test: {
      type: Object,
      required: true
    },
    assignedTestId: {
      type: [String, Number],
      required: true
    },
    initialProgress: {
      type: Object,
      default: () => ({ currentQuestion: 0, answers: {} })
    },
    timeLimit: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      currentQuestionIndex: this.initialProgress.currentQuestion || 0,
      ratings: this.initialProgress.answers || {},
      timeRemaining: this.timeLimit * 60,
      timer: null
    }
  },

  computed: {
    currentQuestion() {
      return this.test.questions[this.currentQuestionIndex]
    },

    isLastQuestion() {
      return this.currentQuestionIndex === this.test.questions.length - 1
    },

    formattedInstructions() {
      return this.test.instructions.replace(/\n/g, '<br>')
    },

    isCurrentGroupComplete() {
      if (!this.currentQuestion) return false
      
      const currentRatings = this.ratings[this.currentQuestion.id] || {}
      const usedRatings = Object.values(currentRatings)
      
      return usedRatings.length === 4
    }
  },

  methods: {
    getRatingForAnswer(answerId) {
      if (!this.currentQuestion) return null
      const questionRatings = this.ratings[this.currentQuestion.id] || {}
      return questionRatings[answerId] || null
    },

    async saveProgress() {
      if (!this.assignedTestId) {
        console.error('ID du test assigné manquant')
        return
      }

      try {
        await this.$http.post(`/tests/${this.assignedTestId}/save-progress`, {
          current_question: this.currentQuestionIndex,
          answers: this.ratings
        })
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },

    async setRating(answerId, rating) {
      const questionId = this.currentQuestion.id
      if (!this.ratings[questionId]) {
        this.ratings[questionId] = {}
      }

      // Si le candidat clique sur le même rating déjà sélectionné, le désélectionner
      if (this.ratings[questionId][answerId] === rating) {
        delete this.ratings[questionId][answerId]
        this.saveProgress()
        return
      }

      // Assigner le rating au critère sélectionné
      this.ratings[questionId][answerId] = rating

      try {
        await this.saveProgress()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la progression:', error)
      }
    },

    async nextQuestion() {
      if (this.isLastQuestion) {
        await this.submitTest()
      } else {
        this.currentQuestionIndex++
      }
    },

    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
      }
    },

    async submitTest() {
      if (!this.assignedTestId) {
        console.error('ID du test assigné manquant')
        return
      }

      try {
        // Transformation des réponses au format attendu par l'API
        const formattedAnswers = []

        // Sécuriser contre des valeurs null / invalides dans ratings
        const ratings = this.ratings && typeof this.ratings === 'object' ? this.ratings : {}

        Object.entries(ratings).forEach(([questionId, questionRatings]) => {
          if (!questionRatings || typeof questionRatings !== 'object') {
            return
          }

          Object.entries(questionRatings).forEach(([answerId, rating]) => {
            if (rating == null) {
              return
            }

            formattedAnswers.push({
              question_id: parseInt(questionId),
              answer_id: parseInt(answerId),
              rating: parseInt(rating)
            })
          })
        })

        console.log('Données formatées:', formattedAnswers);
        
        const response = await this.$http.post(`/tests/${this.assignedTestId}/submit`, {
          application_id: this.assignedTestId,
          answers: formattedAnswers
        });

        console.log('Réponse du serveur:', response.data);

        if (response.data.job_application_id) {
          this.$emit('completed', response.data.job_application_id);
        } else {
          throw new Error('ID de candidature non trouvé dans la réponse');
        }
      } catch (error) {
        console.error('Erreur lors de la soumission du test:', error);
        throw new Error('Erreur lors de la soumission du test');
      }
    },

    startTimer() {
      if (this.timeLimit > 0) {
        this.timer = setInterval(() => {
          if (this.timeRemaining > 0) {
            this.timeRemaining--
          } else {
            clearInterval(this.timer)
            this.submitTest()
          }
        }, 1000)
      }
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }
  },

  mounted() {
    this.startTimer()
  },

  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }
}
</script>

<style scoped>
.behavioral-test {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.test-header {
  margin-bottom: 30px;
}

.instructions {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  line-height: 1.6;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 10px;
  background-color: #e9ecef;
  border-radius: 8px;
}

.question-progress {
  font-weight: bold;
  color: #495057;
}

.time-remaining {
  font-weight: bold;
  color: #2596be;
}

.group-title {
  margin-bottom: 20px;
  color: #333;
  font-size: 1.2rem;
}

.rating-grid {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.rating-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 10px;
}

.trait-label {
  flex: 1;
  font-weight: bold;
}

.rating-numbers {
  display: flex;
  gap: 20px;
  width: 200px;
  justify-content: space-between;
}

.rating-number {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #2596be;
}

.rating-rows {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.rating-row {
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.trait-text {
  flex: 1;
}

.rating-options {
  display: flex;
  gap: 20px;
  width: 200px;
  justify-content: space-between;
}

.rating-cell {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #2596be;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inner-circle {
  width: 10px;
  height: 10px;
  background-color: #2596be;
  border-radius: 50%;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-weight: 500;
}

.btn i {
  font-size: 0.9em;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>